import React, { useState, useMemo, useCallback } from 'react';
import './ConversationAnalysisWorkspace.scss';
import { useStore as useGlobalStore } from 'store/useGlobalStore';
import { BaseWidget } from 'models/WidgetModel';
import { SentimentOption, CategoryFilterItem } from '../shared/types';
import { SessionExplorerSession } from '../shared/sessionExplorerList';
import SessionListPanel from '../SessionListPanel/SessionListPanel';
import SessionDetailHeader from '../SessionDetailHeader/SessionDetailHeader';
import ConversationPanel from '../ConversationPanel/ConversationPanel';
import ConversationFilter from '../ConversationPanel/ConversationFilter/ConversationFilter';
import AiInsightsRail from '../AiInsightsRail/AiInsightsRail';

interface ClusterSentiment {
  icon: string;
  color: string;
  formatted: string;
}

function getClusterSentiment(val: number): ClusterSentiment {
  const formatted = (val > 0 ? '+' : '') + val.toFixed(2);
  if (val >= 0.15) return { icon: 'sentiment_satisfied', color: '#0D6B4D', formatted };
  if (val <= -0.5) return { icon: 'sentiment_very_dissatisfied', color: '#A01B2D', formatted };
  if (val <= -0.15) return { icon: 'sentiment_dissatisfied', color: '#CC3246', formatted };
  return { icon: 'sentiment_neutral', color: '#6C6D6E', formatted };
}

interface ConversationAnalysisWorkspaceProps extends BaseWidget {
  secondaryCategory: any;
  onChangeCategory: (cat: any) => void;
  onRequestClose: () => void;
}

function parseNumber(val: any): number | null {
  const n = parseFloat(String(val ?? ''));
  return isNaN(n) ? null : n;
}

const ConversationAnalysisWorkspace: React.FC<ConversationAnalysisWorkspaceProps> = ({
  secondaryCategory: context,
  onRequestClose,
}) => {
  const dashboardConfig = useGlobalStore((state) => state.configDashboard);

  const [sessionSelected, setSessionSelected] = useState<SessionExplorerSession | null>(null);

  // Lifted conversation-filter state so the filter can render in the
  // conversation header and relocate to the sticky session header on scroll.
  const [categoryFilters, setCategoryFilters] = useState<string[]>([]);
  const [sentimentFilter, setSentimentFilter] = useState<SentimentOption>('all');
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterItems, setFilterItems] = useState<CategoryFilterItem[]>([]);
  const [filterStuck, setFilterStuck] = useState(false);

  const handleClearFilters = useCallback(() => {
    setCategoryFilters([]);
    setSentimentFilter('all');
  }, []);
  const handleFilterToggle = useCallback(() => setFilterOpen((v) => !v), []);
  const handleFilterItemsChange = useCallback((items: CategoryFilterItem[]) => {
    setFilterItems(items);
  }, []);
  const handleInlineFilterVisibility = useCallback((visible: boolean) => {
    setFilterStuck(!visible);
  }, []);

  const sessionId = sessionSelected?.session_id ?? null;

  const handleSessionClear = useCallback(() => {
    setSessionSelected(null);
    setCategoryFilters([]);
    setSentimentFilter('all');
    setFilterOpen(false);
    setFilterStuck(false);
  }, []);

  const handleSessionSelect = useCallback((session: SessionExplorerSession) => {
    setSessionSelected(session);
    // Reset filters for the newly selected session.
    setCategoryFilters([]);
    setSentimentFilter('all');
    setFilterOpen(false);
    setFilterStuck(false);
  }, []);

  // Category-level stats from context
  const totalSessionCount = parseNumber(context?.totalcommentcount ?? context?.totalsessioncount);
  const categoryAvgSentiment = parseNumber(context?.sentiment);
  const l1Name: string = context?.l1categoryname ?? '';
  const l2Name: string = context?.l2categoryname ?? '';

  const sentimentDisplay = useMemo<ClusterSentiment | null>(
    () => (categoryAvgSentiment != null ? getClusterSentiment(categoryAvgSentiment) : null),
    [categoryAvgSentiment]
  );

  return (
    <div className="ca-workspace">
      {/* Top cluster bar */}
      <div className="ca-workspace__cluster-bar">
        {/* Row 1: breadcrumb + close */}
        <div className="ca-workspace__cluster-breadcrumb">
          <div className="ca-workspace__cluster-crumbs">
            {l1Name && (
              <>
                <span className="ca-workspace__cluster-l1">{l1Name}</span>
                <span className="material-icons ca-workspace__cluster-sep" aria-hidden="true">
                  chevron_right
                </span>
              </>
            )}
            <span className="ca-workspace__cluster-l2-crumb">{l2Name}</span>
          </div>

          <button
            className="ca-workspace__close"
            onClick={onRequestClose}
            aria-label="Close conversation analysis"
            type="button"
          >
            <span className="material-icons" aria-hidden="true">
              close
            </span>
          </button>
        </div>

        {/* Row 2: title + stats */}
        <div className="ca-workspace__cluster-row">
          <span className="ca-workspace__cluster-title">{l2Name}</span>

          <div className="ca-workspace__cluster-stats">
            {totalSessionCount != null && (
              <div className="ca-workspace__cluster-stat">
                <span className="ca-workspace__cluster-stat-label">SESSIONS</span>
                <span className="ca-workspace__cluster-stat-value">
                  {totalSessionCount.toLocaleString()}
                </span>
              </div>
            )}
            {sentimentDisplay && (
              <div className="ca-workspace__cluster-stat">
                <span className="ca-workspace__cluster-stat-label">AVG SENTIMENT</span>
                <span
                  className="ca-workspace__cluster-sentiment"
                  style={{ color: sentimentDisplay.color }}
                >
                  <span
                    className="material-icons ca-workspace__cluster-sentiment-icon"
                    aria-hidden="true"
                  >
                    {sentimentDisplay.icon}
                  </span>
                  {sentimentDisplay.formatted}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Three-panel body */}
      <div className="ca-workspace__body">
        {/* Left rail: Session list */}
        <SessionListPanel
          context={context}
          onSessionSelect={handleSessionSelect}
          onSessionClear={handleSessionClear}
          selectedSession={sessionSelected}
        />

        {/* Center: Session detail + conversation */}
        <div className="ca-workspace__center">
          {sessionSelected ? (
            <>
              <SessionDetailHeader
                session={sessionSelected}
                actions={
                  filterStuck && filterItems.length > 0 ? (
                    <ConversationFilter
                      categories={filterItems}
                      categoryFilters={categoryFilters}
                      sentimentFilter={sentimentFilter}
                      onCategoryFiltersChange={setCategoryFilters}
                      onSentimentFilterChange={setSentimentFilter}
                      onClear={handleClearFilters}
                      isOpen={filterOpen}
                      onToggle={handleFilterToggle}
                    />
                  ) : undefined
                }
              />
              <div className="ca-workspace__center-scroll">
                <div className="ca-workspace__center-inner">
                  <ConversationPanel
                    key={sessionId}
                    sessionId={String(sessionId)}
                    isExternal={dashboardConfig.external}
                    categoryFilters={categoryFilters}
                    sentimentFilter={sentimentFilter}
                    filterOpen={filterOpen && !filterStuck}
                    onCategoryFiltersChange={setCategoryFilters}
                    onSentimentFilterChange={setSentimentFilter}
                    onFilterToggle={handleFilterToggle}
                    onClear={handleClearFilters}
                    onFilterItemsChange={handleFilterItemsChange}
                    onInlineFilterVisibility={handleInlineFilterVisibility}
                  />
                </div>
              </div>
            </>
          ) : (
            <div className="ca-workspace__center-empty">
              <span className="material-icons">touch_app</span>
              Select a session from the list to view details
            </div>
          )}
        </div>

        {/* Right rail: AI Insights */}
        <AiInsightsRail
          key={sessionId ?? 'no-session'}
          sessionId={sessionId ? String(sessionId) : null}
          isExternal={dashboardConfig.external}
        />
      </div>
    </div>
  );
};

export default ConversationAnalysisWorkspace;
